import axios, { AxiosInstance, AxiosError } from 'axios'
import { getAccessToken } from './authService'
import type { SupportTicketRequest, SupportTicketResponse } from '../../../msp-portal/src/shared/support/SupportTypes'

// API configuration
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api'
const ASSET_READ_RETRY_DELAYS_MS = [250, 750, 1500]
const ASSET_READ_RETRYABLE_STATUS_CODES = new Set([502, 503, 504])

// Create axios instance with extended timeout for group operations
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Create separate client for long-running operations like group creation
const longRunningApiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 120000, // 2 minutes for group creation
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor for function key authentication
apiClient.interceptors.request.use(
  async (config) => {
    // Add function key for Azure Functions authentication
    // Note: Customer identity is validated via certificate in database, not via token
    const functionKey = import.meta.env.VITE_FUNCTION_KEY
    if (functionKey) {
      config.headers['x-functions-key'] = functionKey
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

const sleep = (delayMs: number) => new Promise((resolve) => setTimeout(resolve, delayMs))

const getAssetReadRetryDelayMs = (attempt: number): number => {
  const baseDelayMs = ASSET_READ_RETRY_DELAYS_MS[Math.min(attempt - 1, ASSET_READ_RETRY_DELAYS_MS.length - 1)]
  const jitterMs = Math.floor(Math.random() * 150)
  return baseDelayMs + jitterMs
}

const isRetryableAssetReadError = (error: AxiosError): boolean => {
  if (error.code === 'ECONNABORTED') {
    return true
  }

  if (!error.response) {
    return Boolean(error.request)
  }

  return ASSET_READ_RETRYABLE_STATUS_CODES.has(error.response.status)
}

const getAssetReadErrorDetails = (error: AxiosError) => ({
  status: error.response?.status,
  code: error.code,
  message: error.message,
})

const getWithAssetReadRetry = async <T>(url: string): Promise<T> => {
  let attempt = 0

  while (true) {
    try {
      attempt += 1
      const response = await apiClient.get<T>(url)

      if (attempt > 1) {
        console.warn('Asset read recovered after retry', {
          url,
          attempt,
        })
      }

      return response.data
    } catch (error) {
      const axiosError = error as AxiosError
      const shouldRetry = isRetryableAssetReadError(axiosError) && attempt <= ASSET_READ_RETRY_DELAYS_MS.length

      if (!shouldRetry) {
        throw error
      }

      const delayMs = getAssetReadRetryDelayMs(attempt)
      console.warn('Retrying asset read after transient failure', {
        url,
        attempt,
        delayMs,
        ...getAssetReadErrorDetails(axiosError),
      })

      await sleep(delayMs)
    }
  }
}

// Request interceptor for long-running operations
longRunningApiClient.interceptors.request.use(
  async (config) => {
    // Add function key for Azure Functions authentication
    const functionKey = import.meta.env.VITE_FUNCTION_KEY
    if (functionKey) {
      config.headers['x-functions-key'] = functionKey
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response) {
      // Server responded with error status
      console.error('API Error:', error.response.status, error.response.data)
    } else if (error.request) {
      // Request made but no response
      console.error('Network Error:', error.message)
    } else {
      // Error setting up request
      console.error('Request Error:', error.message)
    }
    return Promise.reject(error)
  }
)

// Response interceptor for long-running operations
longRunningApiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response) {
      // Server responded with error status
      console.error('Long-running API Error:', error.response.status, error.response.data)
    } else if (error.request) {
      // Request made but no response
      console.error('Long-running Network Error:', error.message)
    } else {
      // Error setting up request
      console.error('Long-running Request Error:', error.message)
    }
    return Promise.reject(error)
  }
)

// Type definitions
export interface Customer {
  tenantId: string
  TenantID: string
  CompanyName: string
  PrimaryContact: string
  ContactEmail: string
  SubscriptionTier: string
  LicenseScenario: string
  SubscriptionStartDate: string
  NextBillingDate: string
  IsActive: boolean
  CertificateThumbprint?: string
  CertificateExpiryDate?: string
}

export interface Deployment {
  deploymentID: number
  tenantId: string
  companyName?: string
  licenseScenario: string
  status: string
  startDate: string
  endDate?: string
  totalPolicies: number
  successfulPolicies: number
  failedPolicies: number
  skippedPolicies: number
  errorMessage?: string
  nonPolicyErrors?: DeploymentNonPolicyError[]
  brandingLogos?: BrandingLogosStatus
}

export interface BrandingLogoAssetStatus {
  assetType: string
  isBlocking: boolean
  isWarning: boolean
  success: boolean
  failedGate?: string
  endpoint?: string
  attemptCount: number
  errorMessage?: string
  putAccepted?: BrandingDeploymentGateStatus
  streamReadbackMatched?: BrandingDeploymentGateStatus
  tenantUiConfirmed?: BrandingDeploymentGateStatus
  delayedRecheckScheduled: boolean
  delayedRecheckCompleted: boolean
  delayedRecheckAttemptCount: number
}

export interface BrandingDeploymentGateStatus {
  passed: boolean
  httpStatusCode?: number
  attemptCount: number
  errorDetail?: string
}

export interface BrandingLogosStatus {
  summaryMessage: string
  hasBlockingFailures: boolean
  hasWarnings: boolean
  assets: BrandingLogoAssetStatus[]
}

export interface DeploymentNonPolicyError {
  category: string
  message: string
  details?: unknown
}

export interface PolicyMetadata {
  CustomerPolicyID?: number
  PolicyID: number
  PolicyName: string
  Platform: string
  DeviceTypes?: string // JSON array: ["Windows"], ["macOS"], ["Tenant"], etc.
  PolicyType: string
  GraphApiEndpoint: string
  LicenseRequirement: string
  SecurityLevel: string
  OwnershipType: string
  Phase: number
  RequiredServicePlans?: string
  AssignToAllUsers: boolean
  AssignToAllDevices: boolean
  BlobPath?: string
  DeploymentID?: number
  DeploymentStatus?: string
  DeployedDate?: string
  LastModifiedDate?: string
  SkipReason?: string
}

export interface DeploymentRequest {
  TenantID: string
  LicenseScenario?: string
  SelectedDeviceTypes: string[]
  ApplyBranding?: boolean
  DeployBrandingCustomization?: boolean
  DeployBrandingLogos?: boolean
  ExecutedBy?: string
  MSPTenantID?: string
  EstimatedPolicies?: number
  RetryFailedDeploymentId?: number
}

export interface SkippedPolicyInfo {
  PolicyName: string
  Reason: string
  GuidanceLink: string
}

export interface DeploymentResult {
  success: boolean
  deploymentID?: number
  totalPolicies: number
  successfulPolicies: number
  failedPolicies: number
  skippedPolicies: number
  errors: string[]
  duration: string
  status: string
  skippedPoliciesDetails?: SkippedPolicyInfo[]
}

export interface AIRequest {
  TenantID: string
  Prompt: string
  ExistingPolicyJson?: string
}

export interface AIResponse {
  Success: boolean
  PolicyJson?: string
  Answer?: string
  Explanation?: string
  TokensUsed: number
  EstimatedCost: number
  ErrorMessage?: string
}

export interface AIUsage {
  TotalInteractions: number
  TotalTokens: number
  TotalCost: number
  AvgTokensPerInteraction: number
  FirstInteraction?: string
  LastInteraction?: string
}

export interface SubscriptionInfo {
  tenantId: string
  CompanyName: string
  SubscriptionStatus: string
  SubscriptionTier: string
  EntitlementType?: string
  MonthlyFee: number
  OnboardingDate: string
  NextBillingDate: string
  LastBillingDate?: string
  InitialConfigPurchaseDate?: string
  InitialConfigFee?: number
  InitialConfigLicenseType?: string
  DaysUntilBilling: number
  TotalDeployments: number
  ActivePolicies: number
  TotalAICost?: number
  TenantID?: string
  TenantDomain?: string
  AppRegistrationID?: string
  CertificateThumbprint?: string
  CertificateExpiryDate?: string
  MarketplaceSubscriptionId?: string
  MarketplaceSubscriptionStatus?: string
  MarketplacePlanId?: string
  MarketplaceQuantity?: number
  MarketplaceTermUnit?: string
  MarketplaceTermStartDate?: string
  MarketplaceTermEndDate?: string
  MarketplaceAutoRenew?: boolean
  EnterpriseClientLicense?: string
  ClientLicenseSubscriptionId?: string
  EnterpriseClientLicenseTermUnit?: string
  EnterpriseClientLicenseStartDate?: string
  EnterpriseClientLicenseEndDate?: string
  EnterpriseClientLicenseAutoRenew?: boolean
  CanAccessFullPortal?: boolean
  CanDeployPolicies?: boolean
  CanCreateGroups?: boolean
  CanDeployBrandingOnly?: boolean
  RequiresEnterpriseClientLicense?: boolean
  HasValidEnterpriseClientLicense?: boolean
  EnterpriseClientLicenseExpired?: boolean
  PortalLockReason?: string
}

const getSupportHeaders = async (): Promise<Record<string, string>> => {
  const token = await getAccessToken()
  if (!token) {
    throw new Error('Your session could not be verified. Please sign in again.')
  }

  return {
    Authorization: `Bearer ${token}`,
  }
}

// API Service functions

// Customer endpoints
export const getCustomer = async (tenantId: string): Promise<Customer> => {
  const response = await apiClient.get(`/tenants/${tenantId}`)
  return response.data
}

export const getCustomerPolicies = async (tenantId: string): Promise<PolicyMetadata[]> => {
  const response = await apiClient.get(`/tenants/${tenantId}/policies`)
  // Backend returns { tenantId, policies: [], totalPolicies }
  return response.data.policies || []
}

export const getSubscriptionInfo = async (tenantId: string): Promise<SubscriptionInfo> => {
  const response = await apiClient.get(`/tenants/${tenantId}/subscription`)
  return response.data
}

export const updateCertificate = async (
  tenantId: string,
  thumbprint: string,
  expiryDate: string
): Promise<void> => {
  await apiClient.put(`/tenants/${tenantId}/certificate`, {
    CertificateThumbprint: thumbprint,
    CertificateExpiryDate: expiryDate,
  })
}

export const createSupportTicket = async (
  payload: SupportTicketRequest
): Promise<SupportTicketResponse> => {
  const headers = await getSupportHeaders()
  const response = await apiClient.post('/support/tickets', payload, { headers })
  return response.data
}

// Deployment endpoints
export const startDeployment = async (
  request: DeploymentRequest
): Promise<DeploymentResult> => {
  const response = await apiClient.post('/deployments/start', request)
  return response.data
}

export const getEffectivePolicyPrefix = async (
  tenantId: string,
  mspTenantId: string
): Promise<{ effectivePolicyPrefix: string | null; hasPrefix: boolean }> => {
  const params = new URLSearchParams({ tenantId, mspTenantId })
  const response = await apiClient.get(`/deployments/effective-prefix?${params.toString()}`)
  return response.data
}

export const getDeploymentStatus = async (deploymentId: number): Promise<Deployment> => {
  const response = await apiClient.get(`/deployments/${deploymentId}/status`)
  const raw = response.data as Deployment & { brandingLogos?: unknown }

  return {
    ...raw,
    brandingLogos: normalizeBrandingLogos(raw.brandingLogos),
  }
}

const normalizeBrandingLogos = (raw: unknown): BrandingLogosStatus | undefined => {
  if (!raw || typeof raw !== 'object') {
    return undefined
  }

  const candidate = raw as Record<string, unknown>
  const assetsRaw = getRecordArray(candidate, ['assets', 'Assets'])

  return {
    summaryMessage: getString(candidate, ['summaryMessage', 'SummaryMessage']) ?? '',
    hasBlockingFailures: getBoolean(candidate, ['hasBlockingFailures', 'HasBlockingFailures']) ?? false,
    hasWarnings: getBoolean(candidate, ['hasWarnings', 'HasWarnings']) ?? false,
    assets: assetsRaw.map((asset) => ({
      assetType: getString(asset, ['assetType', 'AssetType']) ?? 'Unknown',
      isBlocking: getBoolean(asset, ['isBlocking', 'IsBlocking']) ?? false,
      isWarning: getBoolean(asset, ['isWarning', 'IsWarning']) ?? false,
      success: getBoolean(asset, ['success', 'Success']) ?? false,
      failedGate: getString(asset, ['failedGate', 'FailedGate']),
      endpoint: getString(asset, ['endpoint', 'Endpoint']),
      attemptCount: getNumber(asset, ['attemptCount', 'AttemptCount']) ?? 0,
      errorMessage: getString(asset, ['errorMessage', 'ErrorMessage']),
      putAccepted: normalizeBrandingGate(asset, ['putAccepted', 'PutAccepted']),
      streamReadbackMatched: normalizeBrandingGate(asset, ['streamReadbackMatched', 'StreamReadbackMatched']),
      tenantUiConfirmed: normalizeBrandingGate(asset, ['tenantUiConfirmed', 'TenantUiConfirmed']),
      delayedRecheckScheduled: getBoolean(asset, ['delayedRecheckScheduled', 'DelayedRecheckScheduled']) ?? false,
      delayedRecheckCompleted: getBoolean(asset, ['delayedRecheckCompleted', 'DelayedRecheckCompleted']) ?? false,
      delayedRecheckAttemptCount: getNumber(asset, ['delayedRecheckAttemptCount', 'DelayedRecheckAttemptCount']) ?? 0,
    })),
  }
}

const normalizeBrandingGate = (
  obj: Record<string, unknown>,
  keys: string[]
): BrandingDeploymentGateStatus | undefined => {
  for (const key of keys) {
    const value = obj[key]
    if (typeof value === 'object' && value !== null) {
      const candidate = value as Record<string, unknown>
      return {
        passed: getBoolean(candidate, ['passed', 'Passed']) ?? false,
        httpStatusCode: getNumber(candidate, ['httpStatusCode', 'HttpStatusCode']),
        attemptCount: getNumber(candidate, ['attemptCount', 'AttemptCount']) ?? 0,
        errorDetail: getString(candidate, ['errorDetail', 'ErrorDetail']),
      }
    }
  }

  return undefined
}

const getString = (obj: Record<string, unknown>, keys: string[]): string | undefined => {
  for (const key of keys) {
    const value = obj[key]
    if (typeof value === 'string') {
      return value
    }
  }
  return undefined
}

const getBoolean = (obj: Record<string, unknown>, keys: string[]): boolean | undefined => {
  for (const key of keys) {
    const value = obj[key]
    if (typeof value === 'boolean') {
      return value
    }
  }
  return undefined
}

const getNumber = (obj: Record<string, unknown>, keys: string[]): number | undefined => {
  for (const key of keys) {
    const value = obj[key]
    if (typeof value === 'number') {
      return value
    }
  }
  return undefined
}

const getRecordArray = (obj: Record<string, unknown>, keys: string[]): Record<string, unknown>[] => {
  for (const key of keys) {
    const value = obj[key]
    if (Array.isArray(value)) {
      return value.filter((entry): entry is Record<string, unknown> => typeof entry === 'object' && entry !== null)
    }
  }

  return []
}

export const getDeploymentHistory = async (tenantId: string): Promise<Deployment[]> => {
  const response = await apiClient.get(`/deployments/tenant/${tenantId}/history`)
  return response.data.deployments || []
}

// As-built JSON for a specific policy
export const getAsBuiltPolicyJson = async (
  tenantId: string,
  deploymentId: number,
  policyName: string
): Promise<any> => {
  const response = await apiClient.get(`/tenants/${tenantId}/as-built/${deploymentId}/policies/${encodeURIComponent(policyName)}`)
  return response.data
}

// AI Assistant endpoints
export const generatePolicy = async (request: AIRequest): Promise<AIResponse> => {
  const response = await apiClient.post('/ai/generate-policy', request)
  return response.data
}

export const askAIQuestion = async (request: AIRequest): Promise<AIResponse> => {
  const response = await apiClient.post('/ai/ask', request)
  return response.data
}

export const validatePolicy = async (
  policyJson: string,
  policyType: string
): Promise<{ isValid: boolean; errors: string[]; message: string }> => {
  const response = await apiClient.post('/ai/validate', {
    PolicyJson: policyJson,
    PolicyType: policyType,
  })
  return response.data
}

export const getAIUsage = async (tenantId: string): Promise<AIUsage> => {
  const response = await apiClient.get(`/tenants/${tenantId}/ai/usage`)
  return response.data
}

export interface ChatMessage {
  role: 'user' | 'assistant'
  content: string
  timestamp: string
}

export const sendAIMessage = async (
  tenantId: string,
  message: string
): Promise<{ response: string; tokensUsed: number; cost: number }> => {
  const response = await apiClient.post('/ai/chat', {
    TenantId: tenantId,
    Message: message,
  })
  return response.data
}

// AI Entitlements - Based on deployed configuration
export interface AIEntitlements {
  tenantId: string
  canGeneratePolicies: boolean
  canDeployPolicies: boolean
  canModifyPolicies: boolean
  canAccessAdvancedFeatures: boolean
  canAskQuestions: boolean
  canViewRecommendations: boolean
  deployedConfiguration: string | null
  subscriptionTier: string
  reason: string
}

export interface UpgradeInfo {
  needsUpgrade: boolean
  currentConfiguration: string
  message: string
  qualifyingConfigurations: string[]
  features: string[]
}

export const getAIEntitlements = async (tenantId: string): Promise<AIEntitlements> => {
  const response = await apiClient.get(`/tenants/${tenantId}/ai/entitlements`)
  return response.data
}

export const getAIUpgradeInfo = async (tenantId: string): Promise<UpgradeInfo> => {
  const response = await apiClient.get(`/tenants/${tenantId}/ai/upgrade-info`)
  return response.data
}

// Asset management endpoints
export interface Asset {
  assetID: number
  tenantId: string
  fileName: string
  fileSize: number
  mimeType: string
  blobUrl: string
  uploadedDate: string
  isActive?: boolean
  assetType?: string
  assetName?: string
}

export const getAssets = async (tenantId: string): Promise<Asset[]> => {
  return getWithAssetReadRetry<Asset[]>(`/tenants/${tenantId}/assets`)
}

export const uploadAsset = async (
  tenantId: string,
  fileName: string,
  fileContent: string,
  mimeType: string,
  assetType?: string
): Promise<{ success: boolean; assetUrl?: string; errorMessage?: string }> => {
  const response = await apiClient.post(`/tenants/${tenantId}/assets/upload`, {
    FileName: fileName,
    FileContent: fileContent,
    ContentType: mimeType,
    AssetType: assetType,
  })
  return response.data
}

// Certificate management endpoints
export interface Certificate {
  CertificateID: number
  tenantId: string
  CertificateName: string
  Thumbprint: string
  ExpirationDate: string
  IsActive: boolean
  CreatedDate: string
}

export const getCertificates = async (tenantId: string): Promise<Certificate[]> => {
  const response = await apiClient.get(`/tenants/${tenantId}/certificates`)
  return response.data
}

export const uploadCertificate = async (
  tenantId: string,
  certificateName: string,
  certificateContent: string,
  password?: string
): Promise<{ success: boolean; certificateId?: number; errorMessage?: string }> => {
  const response = await apiClient.post(`/tenants/${tenantId}/certificates/upload`, {
    CertificateName: certificateName,
    CertificateContent: certificateContent,
    Password: password || '',
  })
  return response.data
}

// Branding configuration endpoints
export interface BrandingConfig {
  TenantID?: string
  BackgroundColor?: string
  HeaderBackgroundColor?: string
  SignInPageText?: string
  UsernameHintText?: string
  CompanyLogoUrl?: string
  CustomAccountResetCredentialsUrl?: string
  CustomCannotAccessYourAccountText?: string
  CustomCannotAccessYourAccountUrl?: string
  CustomForgotMyPasswordText?: string
  CustomPrivacyAndCookiesText?: string
  CustomPrivacyAndCookiesUrl?: string
  CustomResetItNowText?: string
  CustomTermsOfUseText?: string
  CustomTermsOfUseUrl?: string
  LoginPageLayoutConfiguration?: string
  LoginPageTextVisibilitySettings?: string
}

export const getBranding = async (tenantId: string): Promise<BrandingConfig> => {
  const response = await apiClient.get(`/tenants/${tenantId}/branding`)
  return response.data
}

export const updateBranding = async (
  tenantId: string,
  branding: BrandingConfig
): Promise<{ message: string }> => {
  const response = await apiClient.put(`/tenants/${tenantId}/branding`, branding)
  return response.data
}

export const getBrandingIcons = async (tenantId: string): Promise<{ count: number; isComplete: boolean; icons: any[] }> => {
  const response = await apiClient.get(`/tenants/${tenantId}/logo-wizard/icons`)
  return response.data
}

// Certificate management endpoints
export interface CertificateGenerationResult {
  thumbprint: string
  pfxBase64: string
  cerBase64: string
  password: string
  expiryDate: string
}

export interface AppRegistrationResult {
  appId: string
  message: string
}

export interface ConsentVerificationResult {
  consented: boolean
  message?: string
  error?: string
}

export interface KeyVaultUploadResult {
  success: boolean
  message?: string
  warning?: string
  error?: string
}

export interface ConnectionTestResult {
  success: boolean
  message?: string
  error?: string
}

export const generateCertificate = async (
  tenantId: string,
  tenantDomain: string
): Promise<CertificateGenerationResult> => {
  const response = await apiClient.post(`/tenants/${tenantId}/certificate/generate`, {
    tenantDomain,
  })
  return response.data
}

export const createAppRegistration = async (
  tenantId: string,
  adminAccessToken: string,
  cerBase64: string,
  redirectUri?: string
): Promise<AppRegistrationResult> => {
  const payload: any = {
    tenantId,
    adminAccessToken,
    cerBase64,
  }
  if (redirectUri) payload.redirectUri = redirectUri

  const response = await apiClient.post(`/tenants/${tenantId}/certificate/create-app-registration`, payload)
  return response.data
}

export const verifyAdminConsent = async (
  tenantId: string,
  appId: string,
  adminAccessToken: string
): Promise<ConsentVerificationResult> => {
  const response = await apiClient.post(`/tenants/${tenantId}/certificate/verify-consent`, {
    appId,
    adminAccessToken,
  })
  return response.data
}

export interface MSPProvisionerConsentResult {
  provisioner: boolean
  roleTarget: boolean
  allConsented: boolean
  provisionerAppId: string
  roleTargetAppId: string
  message: string
}

export const verifyMSPProvisionerConsent = async (
  tenantId: string,
  adminAccessToken: string
): Promise<MSPProvisionerConsentResult> => {
  const response = await apiClient.post(`/tenants/${tenantId}/certificate/verify-msp-consent`, {
    adminAccessToken,
  })
  return response.data
}

export const uploadToKeyVault = async (
  tenantId: string,
  pfxBase64: string,
  password: string,
  thumbprint: string
): Promise<KeyVaultUploadResult> => {
  const response = await longRunningApiClient.post(`/tenants/${tenantId}/certificate/upload-to-keyvault`, {
    pfxBase64,
    password,
    thumbprint,
  })
  return response.data
}

export const testCertificateConnection = async (
  tenantId: string
): Promise<ConnectionTestResult> => {
  const response = await apiClient.post(`/tenants/${tenantId}/certificate/test-connection`)
  return response.data
}

// Group Management endpoints
export interface GroupVerificationResult {
  allGroupsExist: boolean
  missingGroups: string[]
  missingCount: number
}

export interface GroupCreationResult {
  success: boolean
  errorMessage?: string
  createdGroups: string[]
  existingGroups: string[]
  failedGroups: string[]
  summary: {
    totalRequired: number
    created: number
    existing: number
    failed: number
  }
}

export interface GroupStatusResult {
  allGroupsExist: boolean
  missingCount: number
  status: 'complete' | 'incomplete'
  message: string
}

export interface GroupCreationProgressResponse {
  sessionId: string
  currentPass: number
  currentPassName: string
  progress: {
    currentGroupNumber: number
    totalGroupsToCreate: number
    progressPercentage: number
  }
  status: 'InProgress' | 'Completed' | 'Failed' | 'PartialSuccess'
  statusMessage: string
  isComplete: boolean
  timestamp: string
}

export const createLicenseGroupsPass1Async = async (
  tenantId: string,
  ownerEmailAddress: string,
  selectedDeviceTypes: string[]
): Promise<{ sessionId: string; status: string }> => {
  const response = await longRunningApiClient.post(
    `/tenants/${tenantId}/groups/create-pass1`,
    { ownerEmailAddress, selectedDeviceTypes }
  )
  return response.data
}

export const getGroupCreationStatus = async (
  tenantId: string,
  sessionId: string
): Promise<GroupCreationProgressResponse> => {
  const response = await apiClient.get(
    `/tenants/${tenantId}/groups/creation-status?sessionId=${sessionId}`
  )
  return response.data
}

export const startAssignOwnershipPass3 = async (
  tenantId: string,
  sessionId: string
): Promise<{ success: boolean; sessionId: string; message?: string }> => {
  const response = await longRunningApiClient.post(
    `/tenants/${tenantId}/groups/assign-ownership-pass3?sessionId=${sessionId}`
  )
  return response.data
}

export const startUpdateGroupPropertiesPass4 = async (
  tenantId: string,
  sessionId: string
): Promise<{ success: boolean; sessionId: string; message?: string }> => {
  const response = await longRunningApiClient.post(
    `/tenants/${tenantId}/groups/update-properties-pass4?sessionId=${sessionId}`
  )
  return response.data
}

export const verifyRequiredGroups = async (
  tenantId: string,
  selectedDeviceTypes: string[]
): Promise<GroupVerificationResult> => {
  const params = new URLSearchParams({
    selectedDeviceTypes: selectedDeviceTypes.join(',')
  })
  const response = await apiClient.get(`/tenants/${tenantId}/groups/verify?${params.toString()}`)
  return response.data
}

export const createRequiredGroups = async (
  tenantId: string,
  ownerEmailAddress: string,
  selectedDeviceTypes: string[]
): Promise<GroupCreationResult> => {
  const response = await longRunningApiClient.post(`/tenants/${tenantId}/groups/create`, {
    ownerEmailAddress,
    selectedDeviceTypes,
  })
  return response.data
}

export const createRequiredGroupsWithPolling = async (
  tenantId: string,
  ownerEmailAddress: string,
  selectedDeviceTypes: string[],
  onProgress?: (status: GroupStatusResult) => void
): Promise<GroupCreationResult> => {
  try {
    // Start the group creation process
    const createResponse = await longRunningApiClient.post(`/tenants/${tenantId}/groups/create`, {
      ownerEmailAddress,
      selectedDeviceTypes,
    })

    // If creation completed immediately, return result
    if (createResponse.data.success !== undefined) {
      return createResponse.data
    }

    // Otherwise, poll for status
    return await pollGroupCreationStatus(tenantId, selectedDeviceTypes, onProgress)
  } catch (error: any) {
    // If the creation request itself fails, try polling to see current status
    if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
      console.warn('Group creation request timed out, polling for status...')
      return await pollGroupCreationStatus(tenantId, selectedDeviceTypes, onProgress)
    }
    throw error
  }
}

const pollGroupCreationStatus = async (
  tenantId: string,
  selectedDeviceTypes: string[],
  onProgress?: (status: GroupStatusResult) => void,
  maxAttempts: number = 60, // 5 minutes with 5-second intervals
  intervalMs: number = 5000
): Promise<GroupCreationResult> => {
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    try {
      const status = await getGroupsStatus(tenantId, selectedDeviceTypes)

      // Call progress callback if provided
      if (onProgress) {
        onProgress(status)
      }

      // If all groups exist, creation is complete
      if (status.allGroupsExist) {
        return {
          success: true,
          createdGroups: [], // We don't know which were just created vs existing
          existingGroups: [],
          failedGroups: [],
          summary: {
            totalRequired: 52, // Based on your tenant group config
            created: 0,
            existing: 52,
            failed: 0
          }
        }
      }

      // Wait before next poll
      if (attempt < maxAttempts - 1) {
        await new Promise(resolve => setTimeout(resolve, intervalMs))
      }
    } catch (error) {
      console.warn(`Polling attempt ${attempt + 1} failed:`, error)
      // Continue polling even if individual requests fail
    }
  }

  // If we get here, polling timed out
  throw new Error('Group creation polling timed out. Please check group status manually.')
}

export const getGroupsStatus = async (
  tenantId: string,
  selectedDeviceTypes: string[]
): Promise<GroupStatusResult> => {
  const params = new URLSearchParams({
    selectedDeviceTypes: selectedDeviceTypes.join(',')
  })
  const response = await apiClient.get(`/tenants/${tenantId}/groups/verify?${params.toString()}`)
  const data = response.data as GroupVerificationResult

  return {
    allGroupsExist: Boolean(data.allGroupsExist),
    missingCount: data.missingCount ?? 0,
    status: data.allGroupsExist ? 'complete' : 'incomplete',
    message: data.allGroupsExist
      ? 'All required groups exist'
      : `${data.missingCount ?? 0} required groups are missing`,
  }
}

// Logo Wizard endpoints
export enum BrandingIconType {
  BannerLogo = 'BannerLogo',
  SquareLogoDark = 'SquareLogoDark',
  SquareLogo = 'SquareLogo',
  Favicon = 'Favicon',
  HeaderLogo = 'HeaderLogo',
  BackgroundImage = 'BackgroundImage'
}

export interface LogoValidationResult {
  isValid: boolean
  errorMessage?: string
  width?: number
  height?: number
  format?: string
  aspectRatio?: number
}

export interface LogoGenerationOptions {
  backgroundColor?: string
  padding?: number
  backgroundOpacity?: number
}

export interface LogoGenerationResult {
  success: boolean
  message?: string
  error?: string
  generatedIcons?: Record<string, string>
  uploadedPaths?: string[]
  totalIcons?: number
  hasFailures?: boolean
  failedIcons?: string[]
  iconResults?: Array<{
    iconType: string
    success: boolean
    isCompliant: boolean
    errorMessage?: string
    fileName?: string
    blobUrl?: string
  }>
}

export const validateMasterLogo = async (
  tenantId: string,
  file: File
): Promise<LogoValidationResult> => {
  const fileBuffer = await file.arrayBuffer()
  
  const response = await apiClient.post(
    `/tenants/${tenantId}/logo-wizard/validate?fileName=${encodeURIComponent(file.name)}`,
    fileBuffer,
    {
      headers: {
        'Content-Type': file.type || 'image/png',
      },
    }
  )
  return response.data
}

export const generateBrandingIcons = async (
  tenantId: string,
  file: File,
  options?: LogoGenerationOptions
): Promise<LogoGenerationResult> => {
  const params = new URLSearchParams()
  if (options?.backgroundColor) params.append('backgroundColor', options.backgroundColor)
  if (options?.padding !== undefined) params.append('padding', options.padding.toString())
  if (options?.backgroundOpacity !== undefined) params.append('backgroundOpacity', options.backgroundOpacity.toString())
  params.append('fileName', file.name)
  
  const fileBuffer = await file.arrayBuffer()
  
  const response = await apiClient.post(
    `/tenants/${tenantId}/logo-wizard/generate?${params.toString()}`,
    fileBuffer,
    {
      headers: {
        'Content-Type': file.type || 'image/png',
      },
    }
  )
  return response.data
}

export const previewBrandingIcon = async (
  tenantId: string,
  file: File,
  iconType: BrandingIconType,
  options?: LogoGenerationOptions
): Promise<{ blob: Blob; warningMessage?: string }> => {
  const params = new URLSearchParams()
  if (options?.backgroundColor) params.append('backgroundColor', options.backgroundColor)
  if (options?.padding !== undefined) params.append('padding', options.padding.toString())
  if (options?.backgroundOpacity !== undefined) params.append('backgroundOpacity', options.backgroundOpacity.toString())
  
  const fileBuffer = await file.arrayBuffer()
  
  const response = await apiClient.post(
    `/tenants/${tenantId}/logo-wizard/preview/${iconType}?${params.toString()}`,
    fileBuffer,
    {
      headers: {
        'Content-Type': file.type || 'image/png',
      },
      responseType: 'blob',
    }
  )

  const headers = response.headers as Record<string, string | undefined>
  const warningEnabled = (headers['x-preview-warning'] || '').toLowerCase() === 'true'
  const warningEncoded = headers['x-preview-warning-message']
  const warningMessage = warningEnabled && warningEncoded ? decodeURIComponent(warningEncoded) : undefined

  return {
    blob: response.data,
    warningMessage,
  }
}

export const deleteAllAssets = async (tenantId: string): Promise<{ success: boolean; deletedCount: number; message: string }> => {
  const response = await apiClient.delete(`/tenants/${tenantId}/assets`)
  return response.data
}

// Certificate and Licensing
export interface CertificateStatus {
  hasValidCertificate: boolean
  tenantId?: string
  appId?: string
  hasCertificate: boolean
  certificateFunctional?: boolean
  certificateError?: string
}

export interface TenantLicense {
  skuPartNumber: string
  skuId: string
  servicePlans: string[]
}

export interface TenantLicensesResponse {
  tenantId: string
  licenses: TenantLicense[]
}

export const getCertificateStatus = async (tenantId: string): Promise<CertificateStatus> => {
  const response = await apiClient.get(`/tenants/${tenantId}/certificate-status`)
  return response.data
}

export const getTenantLicenses = async (tenantId: string): Promise<TenantLicensesResponse> => {
  const response = await apiClient.get(`/tenants/${tenantId}/tenant/licenses`)
  return response.data
}

export interface RecommendedScenariosResponse {
  recommended: string
  viableScenarios: string[]
}

export const getRecommendedScenarios = async (tenantId: string): Promise<RecommendedScenariosResponse> => {
  const response = await apiClient.get(`/tenants/${tenantId}/recommended-scenarios`)
  return response.data
}

export interface TenantAccessVerification {
  canAccessTenant: boolean
  tenantId?: string
  hasRequiredPermissions: boolean
  missingPermissions?: string[]
  errorMessage?: string
}

export const verifyTenantAccess = async (tenantId: string): Promise<TenantAccessVerification> => {
  const response = await apiClient.get(`/tenants/${tenantId}/tenant/verify-access`)
  return response.data
}

// Policy Gap Assessment
export interface LicenseInfo {
  skuPartNumber: string
  friendlyName: string
}

export interface RecommendedPolicy {
  policyID: number
  policyName: string
  platform: string
  policyType: string
  requiredServicePlans: string
}

export interface DeployedPolicy {
  customerPolicyID: number
  policyID: number
  policyName: string
  platform: string
  policyType: string
  deploymentStatus: string
  deployedDate: string
}

export interface GapPolicy {
  policyID: number
  policyName: string
  platform: string
  policyType: string
  requiredServicePlans: string
  priority: string
}

export interface PolicyGapAssessment {
  companyName: string
  tenantID: string
  detectedLicenses: LicenseInfo[]
  servicePlans: string[]
  recommendedPolicies: RecommendedPolicy[]
  deployedPolicies: DeployedPolicy[]
  gapPolicies: GapPolicy[]
  totalRecommended: number
  totalDeployed: number
  totalGaps: number
  coveragePercentage: number
}

export const getPolicyGapAssessment = async (tenantId: string): Promise<PolicyGapAssessment> => {
  const response = await apiClient.get(`/tenants/${tenantId}/gap-assessment`)
  return response.data
}

// Health check
export const healthCheck = async (): Promise<{
  status: string
  timestamp: string
  version: string
}> => {
  const response = await apiClient.get('/health')
  return response.data
}

export default apiClient
